import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import clsx from "clsx";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { X } from "lucide-react";
import {
  PurchaseDocDetail,
  PurchaseDocFile,
  PurchaseDocFileState,
  PurchaseDocSignature,
  PurchaseDocSignType,
  PurchaseDocSigning,
} from "types/InvoiceInbox";
import { requestPurchaseDocSignature } from "services/invoice-inbox.service";
import CustomerContactMultiSelect from "features/customer-contact-select/CustomerContactMultiSelect";
import { refusalMessage } from "./invoice-doc-files";
import InvoiceDocEmployeeMultiSelect from "./invoice-doc-employee-multi-select";
import { PRIMARY_FILE_KEY, fileSignState, isPdfFile } from "./invoice-doc-signing";

/**
 * "Request signature": ask for an e-signature on the invoice PDF or on one of
 * its additional files (a work act, typically).
 *
 * Two routes. Dokobit is the qualified e-signature and can reach outside the
 * company — our employees and the supplier's contact persons. Click is the
 * ERP's own simple signature: only employees, who sign on the ERP document
 * page. Which routes exist is the server's call (`signing`); a missing
 * `signing` (older server) means neither.
 */

type Props = {
  companyId: string;
  doc: PurchaseDocDetail["doc"];
  files: PurchaseDocFile[];
  signatures: PurchaseDocSignature[];
  fileStates: Record<string, PurchaseDocFileState>;
  signing: PurchaseDocSigning | undefined;
  onClose: () => void;
  /** After the request went out — reload the document. */
  onSent: () => Promise<unknown> | void;
};

type FileOption = {
  key: string;
  name: string;
  /** Why it cannot be picked, in the reader's words; null = it can. */
  blockedBy: string | null;
};

export default function InvoiceDocSignRequestDialog({
  companyId,
  doc,
  files,
  signatures,
  fileStates,
  signing,
  onClose,
  onSent,
}: Props) {
  const { t, i18n } = useTranslation();
  const exists = (key: string) => Boolean((i18n as any).exists && (i18n as any).exists(key));
  const dokobitAvailable = Boolean(signing && signing.dokobitAvailable);
  const clickAvailable = Boolean(signing && signing.clickAvailable);

  const blockReason = (
    key: string,
    mimeType: string | null,
    fileName: string | null,
    signedCopy = false
  ): string | null => {
    const state = fileSignState(key, signatures, fileStates);
    if (signedCopy || state === "signed") {
      return t("invoice_inbox_sign_file_already_signed", { defaultValue: "already signed" });
    }
    if (state === "pending") {
      return t("invoice_inbox_sign_file_in_progress", { defaultValue: "signing in progress" });
    }
    if (!isPdfFile(mimeType, fileName)) {
      return t("invoice_inbox_sign_file_not_pdf", { defaultValue: "not a PDF — only a PDF can be signed" });
    }
    // No `stored`/local-copy check here: the server's `ensureFiled` uploads a
    // Files row from the local copy at send time and answers
    // FILE_NOT_AVAILABLE if that has nothing either — blocking on it here
    // would refuse a file the server can still file away just fine.
    return null;
  };

  const fileOptions: FileOption[] = [
    {
      key: PRIMARY_FILE_KEY,
      name: doc.fileName || t("invoice_inbox_file_primary", { defaultValue: "Document" }),
      blockedBy: blockReason(PRIMARY_FILE_KEY, doc.mimeType, doc.fileName),
    },
    ...files.map((file) => ({
      key: file.id,
      name: file.fileName || t("invoice_inbox_file_primary", { defaultValue: "Document" }),
      blockedBy: blockReason(file.id, file.mimeType, file.fileName, file.kind === "signed"),
    })),
  ];

  const firstOpen = fileOptions.find((option) => !option.blockedBy);
  const [fileKey, setFileKey] = useState<string | null>(firstOpen ? firstOpen.key : null);
  // A choice between a qualified and a simple signature is made on purpose —
  // preset only when the company has exactly one of them.
  const [signType, setSignType] = useState<PurchaseDocSignType | null>(
    dokobitAvailable && !clickAvailable ? "Dokobit" : clickAvailable && !dokobitAvailable ? "Click" : null
  );
  const [users, setUsers] = useState<string[]>([]);
  const [contacts, setContacts] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Click never goes outside the company: contacts only count for Dokobit.
  const effectiveContacts = signType === "Dokobit" ? contacts : [];

  // The chosen file is re-judged on every render, not only when it was
  // picked: a failed request reloads the document (see `send`), and the file
  // may come back blocked — pending rows the server left behind, or a
  // signature that completed meanwhile. A blocked file must not stay sendable
  // behind a disabled radio, so the selection is dropped and Send goes grey.
  const selectedOption = fileOptions.find((option) => option.key === fileKey) || null;
  const selectedBlockedBy = selectedOption ? selectedOption.blockedBy : null;
  const selectedOpen = Boolean(selectedOption && !selectedBlockedBy);
  useEffect(() => {
    if (fileKey && (!selectedOption || selectedBlockedBy)) setFileKey(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileKey, selectedBlockedBy, selectedOption === null]);

  const canSend = Boolean(selectedOpen && signType && users.length + effectiveContacts.length > 0) && !sending;

  const send = async () => {
    if (!fileKey || !selectedOpen || !signType) return;
    setSending(true);
    setError(null);
    try {
      await requestPurchaseDocSignature(companyId, doc.id, {
        fileKey,
        signType,
        users,
        contacts: effectiveContacts,
      });
      toast.success(t("invoice_inbox_sign_request_sent", { defaultValue: "Signature request sent" }));
      onClose();
      await onSent();
    } catch (err) {
      // The server may leave pending rows behind even though the REQUEST as a
      // whole failed (DOKOBIT_FAILED keeps the session it already opened; the
      // Click loop can send some invites before hitting USER_NOT_MEMBER on a
      // later one) — reload so the panel shows them and Cancel is offered,
      // but leave the dialog open on the error instead of closing it.
      await onSent();
      setError(refusalMessage(t, exists, err));
    } finally {
      setSending(false);
    }
  };

  const typeCards: Array<{
    value: PurchaseDocSignType;
    title: string;
    hint: string;
    available: boolean;
    unavailable: string;
  }> = [
    {
      value: "Dokobit",
      title: "Dokobit",
      hint: t("invoice_inbox_sign_type_dokobit_hint", {
        defaultValue: "qualified e-signature — employees and supplier contacts",
      }),
      available: dokobitAvailable,
      unavailable: t("invoice_inbox_sign_dokobit_unavailable", {
        defaultValue: "Dokobit is not set up for this company",
      }),
    },
    {
      value: "Click",
      title: "Click",
      hint: t("invoice_inbox_sign_type_click_hint", {
        defaultValue: "simple signature inside the ERP — employees only",
      }),
      available: clickAvailable,
      unavailable: t("invoice_inbox_sign_click_unavailable", {
        defaultValue: "Click signing is not available for this company",
      }),
    },
  ];

  const legendClass = "mb-2 text-sm font-medium text-gray-900";

  return (
    <Dialog open onClose={sending ? () => {} : onClose} className="relative z-[1000]">
      <DialogBackdrop className="fixed inset-0 bg-zinc-950/50" />
      <div className="fixed inset-0 flex items-start justify-center p-2 sm:p-6">
        <DialogPanel className="mt-8 flex max-h-[85dvh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-zinc-950/10">
          <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3">
            <DialogTitle className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
              {t("invoice_inbox_sign_request", { defaultValue: "Request signature" })}
            </DialogTitle>
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              aria-label={t("close", { defaultValue: "Close" })}
              className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 max-md:p-3"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-3">
            <fieldset>
              <legend className={legendClass}>
                {t("invoice_inbox_sign_which_file", { defaultValue: "Which file" })}
              </legend>
              <div className="space-y-1">
                {fileOptions.map((option) => (
                  <label
                    key={option.key}
                    className={clsx(
                      "flex items-start gap-2 rounded-md px-2 py-1.5 text-sm max-md:py-3",
                      option.blockedBy ? "cursor-not-allowed text-gray-400" : "cursor-pointer hover:bg-gray-50"
                    )}
                  >
                    <input
                      type="radio"
                      name="sign-file"
                      value={option.key}
                      checked={fileKey === option.key}
                      disabled={Boolean(option.blockedBy) || sending}
                      onChange={() => setFileKey(option.key)}
                      className="mt-0.5 h-4 w-4 shrink-0 border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="min-w-0">
                      <span className={clsx("block truncate", !option.blockedBy && "text-gray-900")}>
                        {option.name}
                      </span>
                      {option.blockedBy && <span className="block text-xs">{option.blockedBy}</span>}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset>
              <legend className={legendClass}>
                {t("invoice_inbox_sign_how", { defaultValue: "Signature type" })}
              </legend>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {typeCards.map((card) => (
                  <label
                    key={card.value}
                    className={clsx(
                      "flex items-start gap-2 rounded-lg border p-3 text-sm",
                      !card.available
                        ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
                        : signType === card.value
                        ? "cursor-pointer border-blue-500 bg-blue-50"
                        : "cursor-pointer border-gray-300 hover:bg-gray-50"
                    )}
                  >
                    <input
                      type="radio"
                      name="sign-type"
                      value={card.value}
                      checked={signType === card.value}
                      disabled={!card.available || sending}
                      onChange={() => setSignType(card.value)}
                      aria-label={card.title}
                      className="mt-0.5 h-4 w-4 shrink-0 border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="min-w-0">
                      <span className={clsx("block font-medium", card.available && "text-gray-900")}>
                        {card.title}
                      </span>
                      <span className={clsx("block text-xs", card.available && "text-gray-600")}>{card.hint}</span>
                      {!card.available && <span className="mt-1 block text-xs">{card.unavailable}</span>}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <div className="space-y-4">
              <InvoiceDocEmployeeMultiSelect
                companyId={companyId}
                value={users}
                onChange={setUsers}
                disabled={sending}
                label={t("invoice_inbox_sign_employees", { defaultValue: "Employees" })}
              />
              {signType === "Dokobit" &&
                (doc.supplier ? (
                  <CustomerContactMultiSelect
                    companyId={companyId}
                    customerId={doc.supplier}
                    value={contacts}
                    onChange={setContacts}
                    disabled={sending}
                    label="invoice_inbox_sign_supplier_contacts"
                  />
                ) : (
                  <p className="text-sm text-gray-500" data-testid="invoice-doc-sign-no-supplier">
                    {t("invoice_inbox_sign_no_supplier", {
                      defaultValue: "Link the supplier first to ask its contacts",
                    })}
                  </p>
                ))}
            </div>
          </div>

          <div className="space-y-2 border-t border-gray-200 px-4 py-3">
            {error && <p className="text-sm text-red-700">{error}</p>}
            <div className="flex gap-2">
              <button
                type="button"
                disabled={!canSend}
                onClick={send}
                className="rounded-md bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-700 disabled:opacity-50 max-md:min-h-11"
              >
                {t("invoice_inbox_sign_send", { defaultValue: "Send request" })}
              </button>
              <button
                type="button"
                disabled={sending}
                onClick={onClose}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 max-md:min-h-11"
              >
                {t("cancel", { defaultValue: "Cancel" })}
              </button>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
